import { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import { useTheme as useCustomTheme } from "./ThemeContext";
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import {
  Dashboard,
  Business,
  ShoppingCart,
  AccountBalance,
  People,
  BarChart,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Settings,
  AccountTree,
  Inventory2,
  Savings,
  MoreHoriz,
  BalanceOutlined,
  CategoryOutlined,
  LabelOutlined,
  Assignment,
  LocalShipping,
  AssignmentReturn,
  Summarize,
  ReceiptLong,
  PersonSearch,
  ViewList,
  RequestQuote,
  ShoppingBag,
  Undo,
  BarChartOutlined,
  GroupWork,
  PeopleAlt,
  ListAlt,
  TableChart,
  MoveToInbox,
  Outbox,
  CompareArrows,
  Timeline,
  Receipt,
  SwapHoriz,
  PrecisionManufacturing,
} from "@mui/icons-material";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useChat } from "./ChatContext";
import { Badge } from "@mui/material";

const HorizontalNavbar = () => {
  const history = useHistory();
  const location = useLocation();
  const { appBarColor } = useCustomTheme();
  const { permissions } = useAuth();
  const { totalUnread } = useChat();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState(null);
  const [activeNestedMenu, setActiveNestedMenu] = useState(null);

  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReportsPermission = permissions.includes('Read Reports');

  const handleMenuOpen = (event, menuId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNestedMenuOpen = (event, menuLabel) => {
    event.stopPropagation();
    // Always set the new anchor and menu - this handles switching between submenus
    setNestedAnchorEl(event.currentTarget);
    setActiveNestedMenu(menuLabel);
  };

  const handleNestedMenuClose = () => {
    setNestedAnchorEl(null);
    setActiveNestedMenu(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMenuClose();
  };

  // Check if a menu item is active based on current route
  const isMenuActive = (item) => {
    const path = location.pathname;
    if (item.route) return path === item.route || path.startsWith(item.route);
    if (item.subItems) {
      return item.subItems.some(sub => {
        if (sub.route) return path === sub.route || path.startsWith(sub.route);
        if (sub.subItems) return sub.subItems.some(s => path === s.route || path.startsWith(s.route));
        return false;
      });
    }
    return false;
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard sx={{ fontSize: 18 }} />,
      route: '/Home',
      permission: true,
    },
    {
      id: 'master',
      label: 'Master',
      icon: <Business sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      // subItems: [
      //   { label: 'Company', route: '/CompanyEdit' },
      //   { label: 'Client', route: '/ClientIndex' },
      //   { label: 'Bank', route: '/BankDashboard' },
      //   { label: 'Staff', route: '/StaffDashboard' },
      //   { label: 'Chart of Accounts', route: '/AccountHeadDashboard' },
      //   {
      //     label: 'Master',
      //     isNested: true,
      //     subItems: [
      //       { label: 'Client', route: '/ClientIndex' },
      //       { label: 'Vendor', route: '/VendorIndex' },
      //     ]
      //   },
      // ],
      subItems: [
        {
          label: 'Account Master',
          isNested: true,
          subItems: [
            { label: 'Account Head', route: '/AccountHeadIndex', icon: <AccountTree sx={{ fontSize: 15 }} /> },
            { label: 'Asset', route: '/AssetIndex', icon: <Inventory2 sx={{ fontSize: 15 }} /> },
            { label: 'Capital', route: '/CapitalIndex', icon: <Savings sx={{ fontSize: 15 }} /> },
            { label: 'Others', route: '/OtherOptionIndex', icon: <MoreHoriz sx={{ fontSize: 15 }} /> },
            { label: 'Sales Executive', route: '/SalesExecutiveIndex', icon: <PeopleAlt sx={{ fontSize: 15 }} /> },
            { label: 'Salesman Dashboard', route: '/SalesManDashboard', icon: <Dashboard sx={{ fontSize: 15 }} /> },
            { label: 'Balance Sheet', route: '/BalanceSheetHeadIndex', icon: <BalanceOutlined sx={{ fontSize: 15 }} /> },
          ]
        },
        { label: 'Bank', route: '/BankIndex' },
        { label: 'Company', route: '/CompanyEdit' },
        {
          label: 'Master',
          isNested: true,
          subItems: [
            { label: 'Client', route: '/ClientIndex', icon: <People sx={{ fontSize: 15 }} /> },
            { label: 'Vendor', route: '/VendorIndex', icon: <Business sx={{ fontSize: 15 }} /> },
          ]
        },
        {
          label: 'Product',
          isNested: true,
          subItems: [
            { label: 'Category', route: '/ProductCategoryIndex', icon: <CategoryOutlined sx={{ fontSize: 15 }} /> },
            { label: 'Brand', route: '/ProductBrandIndex', icon: <LabelOutlined sx={{ fontSize: 15 }} /> },
            { label: 'Item', route: '/ProductItemIndex', icon: <Inventory2 sx={{ fontSize: 15 }} /> },
          ]
        },

        // { label: 'Staff', route: '/StaffDashboard' },

      ],
    },
    {
      id: 'transaction',
      label: 'Transaction',
      icon: <ShoppingCart sx={{ fontSize: 18 }} />,
      permission: hasReadTransactionPermission || hasReadMasterPermission,
      subItems: [
        {
          label: 'Purchase',
          isNested: true,
          subItems: [
            { label: 'Purchase Order', route: '/PurchaseOrderIndex', icon: <Assignment sx={{ fontSize: 15 }} /> },
            { label: 'Purchase', route: '/PurchaseIndex', icon: <LocalShipping sx={{ fontSize: 15 }} /> },
            { label: 'Purchase Return', route: '/PurchaseReturnAgainstIndex', icon: <AssignmentReturn sx={{ fontSize: 15 }} /> },
            // { label: 'Purchase Return', route: '/PurchaseReturnIndex' },
            { isDivider: true, label: 'Reports' },
            { label: 'Purchase Report', route: '/PurchaseReport', icon: <Summarize sx={{ fontSize: 15 }} /> },
            { label: 'PO Status Report', route: '/PurchaseOrderStatusReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            { label: 'Vendor & Item Wise Purchase', route: '/VendorandItemWisePurchaseReport', icon: <PersonSearch sx={{ fontSize: 15 }} /> },
            { label: 'Vendor Ageing', route: '/VendorAgeingReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            // { label: 'Item Wise Purchase', route: '/ItemWisePurchaseReport', icon: <ViewList sx={{ fontSize: 15 }} /> },
            { label: 'Purchase Register', route: '/PurchaseRegister', icon: <BarChart sx={{ fontSize: 15 }} /> },
          ]
        },
        {
          label: 'Sales',
          isNested: true,
          subItems: [
            { label: 'Quotation', route: '/QuotationIndex', icon: <RequestQuote sx={{ fontSize: 15 }} /> },
            { label: 'Sales Order', route: '/SaleOrderIndex', icon: <Assignment sx={{ fontSize: 15 }} /> },
            { label: 'Sales', route: '/SaleIndex', icon: <ShoppingBag sx={{ fontSize: 15 }} /> },
            { label: 'Sales Return', route: '/SalesReturnIndex', icon: <Undo sx={{ fontSize: 15 }} /> },
            { isDivider: true, label: 'Reports' },
            { label: 'Sales Report', route: '/SalesReport', icon: <Summarize sx={{ fontSize: 15 }} /> },
            { label: 'SO Status Report', route: '/SalesOrderReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            { label: 'Customer & Item Wise Sales', route: '/CustomerWiseSalesReport', icon: <PeopleAlt sx={{ fontSize: 15 }} /> },
            { label: 'Customer Ageing', route: '/CustomerAgeingReport', icon: <ReceiptLong sx={{ fontSize: 15 }} /> },
            // { label: 'Item Wise Sales', route: '/ItemWiseSalesReport', icon: <ListAlt sx={{ fontSize: 15 }} /> },
            { label: 'Sales Register', route: '/SalesRegister', icon: <BarChart sx={{ fontSize: 15 }} /> },
            // { label: 'Sales Summary', route: '/SalesSummaryReport', icon: <TableChart sx={{ fontSize: 15 }} /> },
          ]
        },
        {
          label: 'Stock',
          isNested: true,
          subItems: [
            { label: 'Stock Transfer In', route: '/StockTransferInIndex', icon: <MoveToInbox sx={{ fontSize: 15 }} /> },
            { label: 'Stock Transfer Out', route: '/StockTransferOutIndex', icon: <Outbox sx={{ fontSize: 15 }} /> },
            { isDivider: true, label: 'Reports' },
            { label: 'Stock Transfer Report', route: '/StockTransferReport', icon: <CompareArrows sx={{ fontSize: 15 }} /> },
            { label: 'Stock Movement Report', route: '/StockMovementReport', icon: <Timeline sx={{ fontSize: 15 }} /> },
          ]
        },
        { label: 'Production', route: '/ProductionIndex', icon: <PrecisionManufacturing sx={{ fontSize: 15 }} /> },
        { label: 'E-Invoice', route: '/EInvoiceIndex', icon: <Receipt sx={{ fontSize: 15 }} /> },
        { label: 'GST Item Bulk Update', route: '/GSTItemBulkUpdate', icon: <SwapHoriz sx={{ fontSize: 15 }} /> },
      ],
    },
    {
      id: 'accounts',
      label: 'Accounts',
      icon: <AccountBalance sx={{ fontSize: 18 }} />,
      permission: hasReadAccountsPermission,
      subItems: [
        { label: 'Payment & Receipt', route: '/PaymentAndReceiptIndex' },
        { label: 'Journal Entry', route: '/JournalEntryIndex' },
      ],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <BarChart sx={{ fontSize: 18 }} />,
      route: '/ReportsHub',
      permission: hasReadReportsPermission || hasReadMasterPermission,
    },
    {
      id: 'auth',
      label: 'Auth',
      icon: <People sx={{ fontSize: 18 }} />,
      permission: hasReadAdministrationPermission,
      subItems: [
        { label: 'Users', route: '/UserIndex' },
        { label: 'Roles', route: '/RoleIndex' },
        { label: 'Reset Password', route: '/PasswordChange' },
      ],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings sx={{ fontSize: 18 }} />,
      permission: hasReadMasterPermission,
      subItems: [
        { label: 'Theme Settings', route: '/ThemeSettings' },
        { label: 'Logout', route: '/Logout' },
      ],
    },
  ];

  const renderNestedMenu = (parentItem) => (
    <Menu
      anchorEl={nestedAnchorEl}
      open={activeNestedMenu === parentItem.label}
      onClose={handleNestedMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          backgroundColor: appBarColor,
          color: 'white',
          minWidth: '220px',
          maxHeight: '70vh',
          overflowY: 'auto',
          ml: 0.5,
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        },
      }}
    >
      {parentItem.subItems?.map((subItem, idx) => (
        subItem.isDivider ? (
          <Box key={idx} sx={{ my: 0.5 }}>
            <Divider sx={{ borderColor: '#FFD700', borderWidth: '1px' }} />
            <Typography
              sx={{
                color: '#FFD700',
                px: 2,
                py: 0.5,
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {subItem.label}
            </Typography>
          </Box>
        ) : (
          <MenuItem
            key={idx}
            onClick={() => handleNavigation(subItem.route)}
            sx={{
              fontSize: '13px',
              py: 0.75,
              backgroundColor: location.pathname === subItem.route ? 'rgba(255,215,0,0.2) !important' : 'transparent',
              borderLeft: location.pathname === subItem.route ? '3px solid #FFD700' : '3px solid transparent',
              color: location.pathname === subItem.route ? '#FFD700' : 'white',
              fontWeight: location.pathname === subItem.route ? 700 : 400,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1) !important' },
            }}
          >
            {subItem.icon && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', opacity: 0.85 }}>
                {subItem.icon}
              </Box>
            )}
            {subItem.label}
          </MenuItem>
        )
      ))}
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '48px', gap: 0 }}>
      {menuItems.map((item) => {
        if (!item.permission) return null;

        if (item.route && !item.subItems) {
          return (
            <Box
              key={item.id}
              component={NavLink}
              to={item.route}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
                color: 'white', textDecoration: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, height: '100%',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                '&.active': {
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  borderBottom: '2px solid #FFD700',
                },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
            </Box>
          );
        }

        const active = isMenuActive(item);

        return (
          <Box key={item.id}>
            <Box
              onClick={(e) => handleMenuOpen(e, item.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
                color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 500, height: '48px',
                borderBottom: active ? '2px solid #FFD700' : '2px solid transparent',
                backgroundColor: active || activeMenu === item.id ? 'rgba(255,255,255,0.15)' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{item.label}</Typography>
              <KeyboardArrowDown sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={activeMenu === item.id}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  backgroundColor: appBarColor, color: 'white', minWidth: '200px',
                  mt: 0, borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                },
              }}
            >
              {item.subItems?.map((subItem, idx) => (
                subItem.isNested ? (
                  <Box key={idx}>
                    <MenuItem
                      onClick={(e) => handleNestedMenuOpen(e, subItem.label)}
                      sx={{
                        fontSize: '13px', py: 1, display: 'flex', justifyContent: 'space-between',
                        backgroundColor: subItem.subItems?.some(s => location.pathname === s.route)
                          ? 'rgba(255,215,0,0.15) !important'
                          : 'transparent',
                        borderLeft: subItem.subItems?.some(s => location.pathname === s.route)
                          ? '3px solid #FFD700'
                          : '3px solid transparent',
                        color: subItem.subItems?.some(s => location.pathname === s.route) ? '#FFD700' : 'white',
                        fontWeight: subItem.subItems?.some(s => location.pathname === s.route) ? 700 : 400,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1) !important' },
                        ...(activeNestedMenu === subItem.label && { backgroundColor: 'rgba(255,255,255,0.1) !important' }),
                      }}
                    >
                      {subItem.label}
                      <KeyboardArrowRight sx={{ fontSize: 16 }} />
                    </MenuItem>
                    {renderNestedMenu(subItem)}
                  </Box>
                ) : (
                  <MenuItem
                    key={idx}
                    onClick={() => handleNavigation(subItem.route)}
                    sx={{
                      fontSize: '13px', py: 1,
                      backgroundColor: location.pathname === subItem.route ? 'rgba(255,215,0,0.2) !important' : 'transparent',
                      borderLeft: location.pathname === subItem.route ? '3px solid #FFD700' : '3px solid transparent',
                      color: location.pathname === subItem.route ? '#FFD700' : 'white',
                      fontWeight: location.pathname === subItem.route ? 700 : 400,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1) !important' },
                    }}
                  >
                    {subItem.label}
                  </MenuItem>
                )
              ))}
            </Menu>
          </Box>
        );
      })}

      {/* ── Chat Button ── */}
      <Box
        component={NavLink}
        to="/InternalMessaging"
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, px: 2, py: 1,
          color: 'white', textDecoration: 'none', cursor: 'pointer',
          fontSize: '13px', fontWeight: 500, height: '100%',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
          '&.active': { backgroundColor: 'rgba(255,255,255,0.18)', borderBottom: '2px solid #FFD700' },
        }}
      >
        <Badge
          badgeContent={totalUnread > 0 ? (totalUnread > 99 ? '99+' : totalUnread) : null}
          sx={{ '& .MuiBadge-badge': { background: '#ef4444', color: '#fff', fontSize: 10, minWidth: 16, height: 16, padding: '0 3px' } }}
        >
          <span style={{ fontSize: 18 }}>💬</span>
        </Badge>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, ml: 0.5 }}>Chat</Typography>
      </Box>
    </Box>
  );
};

export default HorizontalNavbar;
